import { useState, useMemo, useEffect, useRef, Fragment, type ReactNode } from 'react';
import { MirrorFreshnessBanner } from '../components/common/MirrorFreshnessBanner';
import { useNavigate } from 'react-router-dom';
import { RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchClientReview } from '../hooks/useReviews';
import { PageSkeleton } from '../components/common/PageSkeleton';
import {
  Card,
  Title1,
  Text,
  Spinner,
  Badge,
  Button,
  Dropdown,
  Option,
  Combobox,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Input,
  Radio,
  RadioGroup,
  tokens,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tab,
  TabList,
  Tooltip,
  Checkbox,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { LoadFailed } from '../components/common/LoadFailed';
import {
  AddRegular,
  DocumentSearchRegular,
  ArrowSyncRegular,
  OpenRegular,
  LightbulbRegular,
  CalendarRegular,
  TaskListLtr24Regular,
  CheckmarkRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { useClientReviews, useCreateReview, useSuggestedReviews } from '../hooks/useReviews';
import { useReviewerAssignments, useStaffProfiles } from '../api/staffProfiles';
import { useBookkeepingClients } from '../hooks/useClients';
import { useTableFeatures } from '../hooks/useTableFeatures';
import { useReviewerEligibleStaff, useSystemUsers } from '../hooks/useSystemUsers';
import { SortableHeader, TableToolbar } from '../components/common';
import {
  FilterColumn, SavedView, useListView, QuickViewPills, ActiveFiltersChips, ListColumnHeader,
} from '../components/listFilters';
import { groupRows, isGroupable } from '../components/listFilters/grouping';
import { useColumnPrefs } from '../components/listFilters/useColumnPrefs';
import { BulkActionBar, useReviewSelection } from '../components/reviews/ReviewBulkActions';
import { applyDrill, useProductivityDrill } from '../components/productivity/productivityDrill';
import { DrillBanner } from '../components/productivity/DrillBanner';
import { SearchRegular } from '@fluentui/react-icons';
import { parseISO, formatDistanceToNow } from 'date-fns';
import i18n, { useTranslation } from '../i18n';
import { dateFnsLocale, formatInstant } from '../i18n/dates';
import { formatCalendarDate, formatPeriodRange, nextCalendarDay, firstOfMonthMonthsAgo, lastDayOfPreviousMonth } from '../utils/calendarDate';
import { taskWindowLabel } from '../utils/taskWindow';
import { reviewPeriodBoundsError } from '../utils/reviewPeriod';
import type { BookkeepingRecord, SystemUser } from '../types';
import type { SuggestedReview, ClientReview } from '../api/reviews';
import { buildSuggestionSnapshot } from '../utils/suggestionSnapshot';
import { BUILD_COMMIT } from '../buildInfo';
import { ClientLanguageBadge } from '../components/common/ClientLanguageBadge';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge appearance="filled" color="success">{i18n.t('reviewStatus.completed')}</Badge>;
    case 'in-progress':
      return <Badge appearance="filled" color="brand">{i18n.t('reviewStatus.inProgress')}</Badge>;
    case 'ready':
      return <Badge appearance="tint" color="warning">{i18n.t('reviewStatus.ready')}</Badge>;
    // `scheduled` is created-and-visible but not yet actionable — deliberately the quietest badge
    // on the page so it never reads as work owed today.
    case 'scheduled':
      return <Badge appearance="outline" color="informative">{i18n.t('reviewStatus.scheduled')}</Badge>;
    // DP-READY-3: the period ended but its bookkeeping is still open — not owed yet, so not the "ready" colour.
    case 'waiting-on-bookkeeping':
      return <Badge appearance="outline" color="warning">{i18n.t('reviewStatus.waitingOnBookkeeping')}</Badge>;
    // Legacy: a stored `pending` reaching the badge without a derived status (older API response).
    case 'pending':
      return <Badge appearance="tint" color="warning">{i18n.t('reviewStatus.pending')}</Badge>;
    case 'cancelled':
      return <Badge appearance="tint" color="subtle">{i18n.t('reviewStatus.cancelled')}</Badge>;
    default:
      return <Badge appearance="outline">{status}</Badge>;
  }
}

function getComplexityBadge(complexity?: string) {
  switch (complexity) {
    case 'high':
      return <Badge appearance="filled" color="danger">{i18n.t('complexity.label.high')}</Badge>;
    case 'medium':
      return <Badge appearance="filled" color="warning">{i18n.t('complexity.label.medium')}</Badge>;
    case 'low':
      return <Badge appearance="filled" color="success">{i18n.t('complexity.label.low')}</Badge>;
    default:
      return <Badge appearance="outline">-</Badge>;
  }
}

type ReviewType = 'quarterly' | 'year-end';

/**
 * The signed-in user, but only when they are someone the reviewer picker would actually OFFER.
 *
 * The Create Review dialog pre-fills the supervisor with whoever is signed in, and falls back to
 * them again at submit when nothing is selected. Narrowing the picker without narrowing that
 * default would close the front door and leave the side door open: a review could still be stamped
 * with someone the picker refuses to list, and — because the default is silent — it would look
 * completely normal, which is the exact shape of the defect this is fixing.
 *
 * Returns null while the eligible list is still loading (`undefined`): defaulting to someone before
 * we know whether they may be offered is the guess this is here to avoid. A one-render delay in the
 * pre-fill is cheap; a wrong assignee is not.
 */
export function defaultSupervisorFrom(
  currentSystemUser: SystemUser | null | undefined,
  reviewerOptions: SystemUser[] | undefined,
): SystemUser | null {
  if (!currentSystemUser?.systemuserid || !reviewerOptions) return null;
  return reviewerOptions.some(u => u.systemuserid === currentSystemUser.systemuserid)
    ? currentSystemUser
    : null;
}

interface CreateReviewDialogProps {
  open: boolean;
  onClose: () => void;
  clients: BookkeepingRecord[];
  existingReviews: ClientReview[];
  /**
   * The people this dialog may OFFER as the reviewer — `useReviewerEligibleStaff()`, NOT the staff
   * roster. Named for what it is so nobody later "fixes" it by passing `useSystemUsers()` back in:
   * the roster carries every enabled person, including staff explicitly excluded from supervision.
   */
  reviewerOptions: SystemUser[];
  /** True when the reviewer list FAILED to load: the empty picker then says so, not "no eligible staff". */
  reviewerOptionsFailed: boolean;
  /**
   * False until the existing reviews have loaded. The overlap and last-review checks read them, so
   * Create is held until they are known: an empty list that is really a failed one passes every check (P6).
   */
  existingReviewsKnown: boolean;
  onSubmit: (data: {
    bookkeepingId: string;
    clientId: string;
    clientName: string;
    bookkeeperId: string;
    bookkeeperName: string;
    supervisorId: string;
    supervisorName: string;
    xeroLink?: string;
    avgMonthlyTransactions?: number;
    periodStart: string;
    periodEnd: string;
    reviewType: ReviewType;
  }) => Promise<void>;
  preselectedClient?: SuggestedReview | null;
  currentSystemUser?: SystemUser | null;
  isSubmitting?: boolean;
  currentUser: { id: string; name: string } | null;
  /**
   * Resolves the assigned reviewer's Dataverse systemuserid for a client (by account GUID), from
   * the ReviewerAssignment table (Task 7). Returns undefined when there is no assignment (or the
   * caller can't read assignments — non-admins get a 403 and the lookup is simply absent), in which
   * case the dialog falls back to defaulting the supervisor to the current user — but only when
   * the current user is themselves reviewer-eligible (see defaultSupervisorFrom).
   */
  assignedReviewerSystemUserIdFor?: (companyId?: string) => string | undefined;
}

function CreateReviewDialog({ open, onClose, clients, existingReviews, reviewerOptions, reviewerOptionsFailed, existingReviewsKnown, onSubmit, preselectedClient, isSubmitting, currentUser, currentSystemUser, assignedReviewerSystemUserIdFor }: CreateReviewDialogProps) {
  const { t } = useTranslation();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [reviewType, setReviewType] = useState<ReviewType>('quarterly');
  // Use the Dataverse systemuserid (not Azure AD ID) for proper email lookup
  const [selectedSupervisorId, setSelectedSupervisorId] = useState(
    defaultSupervisorFrom(currentSystemUser, reviewerOptions)?.systemuserid || '');
  // Once the user manually picks a supervisor we stop auto-suggesting so their choice sticks.
  const userPickedSupervisor = useRef(false);
  const [supervisorSearchQuery, setSupervisorSearchQuery] = useState('');
  const [isSupervisorSearchFocused, setIsSupervisorSearchFocused] = useState(false);

  // The signed-in user AS A DEFAULT — only when the picker would actually offer them. Every
  // "fall back to me" path below goes through this rather than through `currentSystemUser`, so
  // narrowing the picker cannot be quietly undone by the default. See defaultSupervisorFrom.
  const eligibleCurrentUser = useMemo(
    () => defaultSupervisorFrom(currentSystemUser, reviewerOptions),
    [currentSystemUser, reviewerOptions]);

  // Find the selected supervisor from the list
  const selectedSupervisor = reviewerOptions.find(u => u.systemuserid === selectedSupervisorId);
  const supervisorName = selectedSupervisor?.fullname || eligibleCurrentUser?.fullname || '';

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!supervisorSearchQuery) return reviewerOptions;
    const query = supervisorSearchQuery.toLowerCase();
    return reviewerOptions.filter(user =>
      user.fullname.toLowerCase().includes(query) ||
      (user.internalemailaddress?.toLowerCase().includes(query))
    );
  }, [reviewerOptions, supervisorSearchQuery]);

  // Update selectedSupervisorId once the caller resolves AND is known to be reviewer-eligible
  // (initial state may be empty because either list was still loading).
  useEffect(() => {
    if (eligibleCurrentUser?.systemuserid && !selectedSupervisorId) {
      setSelectedSupervisorId(eligibleCurrentUser.systemuserid);
    }
  }, [eligibleCurrentUser, selectedSupervisorId]);

  // Fresh open → allow auto-suggestion of the assigned reviewer again.
  useEffect(() => {
    if (open) userPickedSupervisor.current = false;
  }, [open]);

  // The account GUID of the client currently in the form (used to look up its assigned reviewer).
  const currentCompanyId = preselectedClient
    ? preselectedClient.clientId
    : clients.find(c => c.cr8f7_bookkeepingid === selectedClientId)?._cr8f7_client_value;

  // Prefer the client's assigned reviewer (ReviewerAssignment, Task 7) as the default supervisor.
  // Old behaviour (default to the current user) remains the fallback when there is no assignment or
  // the caller can't read assignments. Skipped once the user manually picks a supervisor.
  useEffect(() => {
    if (userPickedSupervisor.current) return;
    const assigned = assignedReviewerSystemUserIdFor?.(currentCompanyId);
    if (assigned) {
      setSelectedSupervisorId(assigned);
    }
  }, [currentCompanyId, assignedReviewerSystemUserIdFor]);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear - 1); // Default to previous year for year-end
  const [periodStart, setPeriodStart] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    const date = new Date();
    date.setDate(0); // Last day of previous month
    return date.toISOString().split('T')[0];
  });

  const selectedClient = preselectedClient
    ? null // We'll use preselectedClient data directly
    : clients.find(c => c.cr8f7_bookkeepingid === selectedClientId);

  // Get the bookkeeping ID for the current selection
  const currentBookkeepingId = preselectedClient?.bookkeepingId || selectedClientId;

  // Find the last review for this client to auto-set the start date
  const lastReviewForClient = useMemo(() => {
    if (!currentBookkeepingId) return null;
    const clientReviews = existingReviews.filter(r => r.bookkeepingId === currentBookkeepingId);
    if (clientReviews.length === 0) return null;
    // Sort by periodEnd descending to get the most recent
    return clientReviews.sort((a, b) =>
      new Date(b.periodEnd).getTime() - new Date(a.periodEnd).getTime()
    )[0];
  }, [currentBookkeepingId, existingReviews]);

  // Check for overlapping reviews
  const overlappingReview = useMemo(() => {
    if (!currentBookkeepingId) return null;
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    return existingReviews.find(r => {
      if (r.bookkeepingId !== currentBookkeepingId) return false;
      const reviewStart = new Date(r.periodStart);
      const reviewEnd = new Date(r.periodEnd);
      // Check for any overlap
      return (start <= reviewEnd && end >= reviewStart);
    });
  }, [currentBookkeepingId, periodStart, periodEnd, existingReviews]);

  // Check if dates are before last review
  const isBeforeLastReview = useMemo(() => {
    if (!lastReviewForClient) return false;
    return new Date(periodStart) < new Date(lastReviewForClient.periodEnd);
  }, [periodStart, lastReviewForClient]);

  const [showWarning, setShowWarning] = useState(false);

  // Hard stop on malformed bounds (mirrors the server's ReviewPeriodValidator, which now 400s).
  // Unlike the overlap/backdate warnings below this is NOT confirm-to-override: a period that ends
  // before it starts is unrepresentable, not a judgement call. It can appear with no user input at
  // all — the auto-fill effect above takes periodStart from "the day after the last review" and
  // periodEnd from "the last day of the previous month", which cross over whenever the previous
  // review already ran that far (this is how 2026-03-01 .. 2026-02-28 was created).
  const periodBoundsError = useMemo(
    () => reviewPeriodBoundsError(periodStart, periodEnd),
    [periodStart, periodEnd]
  );

  // Auto-update period dates when client changes or review type changes.
  // useEffect (not useMemo) — this only performs setState side effects and returns nothing.
  useEffect(() => {
    if (reviewType === 'year-end') {
      // For year-end, set to full year
      setPeriodStart(`${selectedYear}-01-01`);
      setPeriodEnd(`${selectedYear}-12-31`);
    } else if (reviewType === 'quarterly') {
      // For quarterly, start from day after last review or 3 months ago
      if (lastReviewForClient) {
        // Calendar arithmetic, never local-time Date mutation + toISOString(): see calendarDate.ts.
        setPeriodStart(nextCalendarDay(lastReviewForClient.periodEnd) ?? firstOfMonthMonthsAgo(3));
      } else {
        setPeriodStart(firstOfMonthMonthsAgo(3));
      }
      // End date is last day of previous month
      setPeriodEnd(lastDayOfPreviousMonth());
    }
  }, [reviewType, selectedYear, lastReviewForClient]);

  const handleSubmit = async () => {
    // Malformed bounds are never submittable — no confirm-to-override path.
    if (periodBoundsError) return;

    // Check for warnings and require confirmation
    if ((overlappingReview || isBeforeLastReview) && !showWarning) {
      setShowWarning(true);
      return;
    }

    // Use Dataverse systemuserid for proper email lookup when sending emails. The "fall back to me"
    // leg goes through eligibleCurrentUser, NOT currentSystemUser: a silent default is exactly how a
    // review ends up assigned to someone the picker would not have offered.
    const supervisorId = selectedSupervisorId || eligibleCurrentUser?.systemuserid || 'unknown';
    const finalSupervisorName = supervisorName || eligibleCurrentUser?.fullname || 'Unknown';

    try {
      if (preselectedClient) {
        await onSubmit({
          bookkeepingId: preselectedClient.bookkeepingId,
          clientId: preselectedClient.clientId,
          clientName: preselectedClient.clientName,
          bookkeeperId: preselectedClient.bookkeeperId,
          bookkeeperName: preselectedClient.bookkeeperName,
          supervisorId,
          supervisorName: finalSupervisorName,
          xeroLink: preselectedClient.xeroLink,
          avgMonthlyTransactions: preselectedClient.avgMonthlyTransactions,
          periodStart,
          periodEnd,
          reviewType,
        });
        setShowWarning(false);
        onClose();
        return;
      }

      if (!selectedClient) return;

      await onSubmit({
        bookkeepingId: selectedClient.cr8f7_bookkeepingid,
        clientId: selectedClient._cr8f7_client_value,
        clientName: selectedClient['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || selectedClient.cr8f7_name,
        bookkeeperId: selectedClient._ownerid_value,
        bookkeeperName: selectedClient['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || 'Unknown',
        supervisorId,
        supervisorName: finalSupervisorName,
        xeroLink: selectedClient.cr8f7_xerolink,
        avgMonthlyTransactions: selectedClient.cr8f7_avgmonthlytransactions,
        periodStart,
        periodEnd,
        reviewType,
      });

      setSelectedClientId('');
      setShowWarning(false);
      onClose();
    } catch (error) {
      console.error('Failed to create review:', error);
      // Error will be handled by the mutation's error state
    }
  };

  // Generate year options (last 5 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogBody>
          <DialogTitle>{t('createReview.title')}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {preselectedClient ? (
                <Card style={{ padding: '12px', backgroundColor: tokens.colorNeutralBackground2 }}>
                  <Text weight="semibold" style={{ display: 'block' }}>
                    {preselectedClient.clientName}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {t('createReview.bookkeeper', { name: preselectedClient.bookkeeperName })}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                    {t('createReview.avgTransactions', { value: preselectedClient.avgMonthlyTransactions?.toLocaleString(i18n.language) || t('common.notAvailable') })}
                  </Text>
                  {preselectedClient.priorityReason && (
                    <Badge appearance="tint" color="warning" style={{ marginTop: '8px' }}>
                      {preselectedClient.priorityReason}
                    </Badge>
                  )}
                </Card>
              ) : (
                <>
                  <Field label={t('createReview.selectClient')} required>
                    <Dropdown
                      placeholder={t('createReview.selectClientPlaceholder')}
                      value={selectedClient ?
                        (selectedClient['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || selectedClient.cr8f7_name)
                        : undefined}
                      onOptionSelect={(_, data) => setSelectedClientId(data.optionValue as string)}
                      style={{ width: '100%' }}
                    >
                      {clients.map(client => {
                        const displayText = `${client['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || client.cr8f7_name} - ${client['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || t('common.unassigned')}`;
                        return (
                          <Option key={client.cr8f7_bookkeepingid} value={client.cr8f7_bookkeepingid} text={displayText}>
                            {displayText}
                          </Option>
                        );
                      })}
                    </Dropdown>
                  </Field>

                  {selectedClient && (
                    <Card style={{ padding: '12px', backgroundColor: tokens.colorNeutralBackground2 }}>
                      <Text weight="semibold" style={{ display: 'block' }}>
                        {selectedClient['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || selectedClient.cr8f7_name}
                      </Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {t('createReview.bookkeeper', { name: selectedClient['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || t('common.unassigned') })}
                      </Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                        {t('createReview.avgTransactions', { value: selectedClient.cr8f7_avgmonthlytransactions?.toLocaleString(i18n.language) || t('common.notAvailable') })}
                      </Text>
                    </Card>
                  )}
                </>
              )}

              <Field label={t('createReview.reviewType')}>
                <RadioGroup
                  value={reviewType}
                  onChange={(_, data) => setReviewType(data.value as ReviewType)}
                  layout="horizontal"
                >
                  <Radio value="quarterly" label={t('createReview.quarterlyOption')} />
                  <Radio value="year-end" label={t('createReview.yearEndOption')} />
                </RadioGroup>
              </Field>

              {reviewType === 'year-end' ? (
                <Field label={t('createReview.reviewYear')}>
                  <Dropdown
                    value={selectedYear.toString()}
                    onOptionSelect={(_, data) => setSelectedYear(parseInt(data.optionValue as string))}
                    style={{ width: '200px' }}
                  >
                    {yearOptions.map(year => (
                      <Option key={year} value={year.toString()} text={year.toString()}>
                        {year}
                      </Option>
                    ))}
                  </Dropdown>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                    {t('createReview.fullYear', { year: selectedYear })}
                  </Text>
                </Field>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <Field label={t('createReview.periodStart')}>
                    <CalendarDateInput
                      value={periodStart}
                      onChange={(value) => { setPeriodStart(value); setShowWarning(false); }}
                    />
                    {lastReviewForClient && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                        {t('createReview.lastEnded', { date: formatCalendarDate(lastReviewForClient.periodEnd) })}
                      </Text>
                    )}
                  </Field>
                  <Field label={t('createReview.periodEnd')}>
                    <CalendarDateInput
                      value={periodEnd}
                      onChange={(value) => { setPeriodEnd(value); setShowWarning(false); }}
                    />
                  </Field>
                </div>
              )}

              {/* Supervisor Assignment */}
              <Field label={t('createReview.assignedTo')}>
                <Combobox
                  placeholder={t('createReview.searchSupervisor')}
                  value={isSupervisorSearchFocused ? supervisorSearchQuery : supervisorName}
                  selectedOptions={selectedSupervisorId ? [selectedSupervisorId] : []}
                  onOptionSelect={(_, data) => {
                    userPickedSupervisor.current = true;
                    setSelectedSupervisorId(data.optionValue as string);
                    setSupervisorSearchQuery('');
                    setIsSupervisorSearchFocused(false);
                  }}
                  onChange={(e) => {
                    setSupervisorSearchQuery(e.target.value);
                    // Clear the selection if user is typing
                    if (e.target.value !== supervisorName) {
                      setSelectedSupervisorId('');
                    }
                  }}
                  onFocus={() => setIsSupervisorSearchFocused(true)}
                  onBlur={() => {
                    // Delay to allow option selection
                    setTimeout(() => setIsSupervisorSearchFocused(false), 200);
                  }}
                  style={{ width: '100%' }}
                  freeform
                >
                  {filteredUsers.map(user => (
                    <Option
                      key={user.systemuserid}
                      value={user.systemuserid}
                      text={user.fullname}
                    >
                      <div>
                        <Text weight="semibold">{user.fullname}</Text>
                        {user.jobtitle && (
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                            {user.jobtitle}
                          </Text>
                        )}
                      </div>
                    </Option>
                  ))}
                </Combobox>
                {/* Shown only when the signed-in user is someone this picker would offer, and it
                    now sets their systemuserid — `currentUser.id` is the Azure AD object id, which
                    is not the id a review's supervisorId holds, so the old shortcut both always
                    rendered (the comparison could never match) and assigned the wrong id. */}
                {eligibleCurrentUser && selectedSupervisorId !== eligibleCurrentUser.systemuserid && (
                  <Button
                    appearance="transparent"
                    size="small"
                    style={{ marginTop: '4px' }}
                    onClick={() => {
                      userPickedSupervisor.current = true;
                      setSelectedSupervisorId(eligibleCurrentUser.systemuserid);
                      setSupervisorSearchQuery('');
                    }}
                  >
                    {t('createReview.assignToMe', { name: eligibleCurrentUser.fullname || currentUser?.name })}
                  </Button>
                )}
                {/* An empty picker is otherwise indistinguishable from a slow one. Say what is
                    actually true — the list is narrowed by a flag an admin controls — without
                    claiming anything about who does or should review. */}
                {reviewerOptions.length === 0 && (
                  <Text size={200} style={{ color: reviewerOptionsFailed ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' }}>
                    {reviewerOptionsFailed ? t('createReview.staffNotLoaded') : t('createReview.noStaff')}
                  </Text>
                )}
              </Field>

              {/* Blocking error for malformed period bounds — shown immediately, not on submit,
                  because the dialog can pre-fill a backwards period before the user touches it. */}
              {periodBoundsError && (
                <Card
                  role="alert"
                  style={{
                    padding: '12px',
                    backgroundColor: tokens.colorPaletteRedBackground1,
                    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
                  }}
                >
                  <Text weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }}>
                    {t('createReview.invalidPeriod')}
                  </Text>
                  <Text size={200} style={{ display: 'block', marginTop: '4px' }}>
                    {t('createReview.fixDates', { error: periodBoundsError })}
                  </Text>
                </Card>
              )}

              {!existingReviewsKnown && (
                <MessageBar intent="warning">
                  <MessageBarBody>{t('createReview.reviewsNotLoaded')}</MessageBarBody>
                </MessageBar>
              )}

              {/* Warning for overlapping or backdated reviews */}
              {showWarning && (overlappingReview || isBeforeLastReview) && (
                <Card style={{ padding: '12px', backgroundColor: tokens.colorPaletteYellowBackground1, border: `1px solid ${tokens.colorPaletteYellowBorder2}` }}>
                  <Text weight="semibold" style={{ color: tokens.colorPaletteYellowForeground2 }}>
                    {t('createReview.conflictTitle')}
                  </Text>
                  {overlappingReview && (
                    <Text size={200} style={{ display: 'block', marginTop: '4px' }}>
                      {t('createReview.overlaps', { range: formatPeriodRange(overlappingReview.periodStart, overlappingReview.periodEnd) })}
                    </Text>
                  )}
                  {isBeforeLastReview && !overlappingReview && (
                    <Text size={200} style={{ display: 'block', marginTop: '4px' }}>
                      {t('createReview.beforeLast', { date: formatCalendarDate(lastReviewForClient!.periodEnd) })}
                    </Text>
                  )}
                  <Text size={200} style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                    {t('createReview.confirmAgain')}
                  </Text>
                </Card>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" disabled={isSubmitting}>{t('common.cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance={showWarning ? 'primary' : 'primary'}
              onClick={handleSubmit}
              disabled={isSubmitting || !existingReviewsKnown || (!preselectedClient && !selectedClientId) || !!periodBoundsError}
              style={showWarning ? { backgroundColor: tokens.colorPaletteYellowBackground3 } : undefined}
            >
              {isSubmitting ? t('createReview.creating') : showWarning ? t('createReview.confirm') : t('createReview.create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function getPriorityBadge(score: number) {
  if (score >= 60) return <Badge appearance="filled" color="danger">{i18n.t('priority.high')}</Badge>;
  if (score >= 40) return <Badge appearance="filled" color="warning">{i18n.t('priority.medium')}</Badge>;
  return <Badge appearance="tint" color="subtle">{i18n.t('priority.low')}</Badge>;
}

interface SuggestedReviewsTabProps {
  suggestions: SuggestedReview[];
  isLoading: boolean;
  /** Set when the suggestions failed to load: said as a failure, never "no suggestions" (P6). */
  loadError: unknown;
  onCreateReview: (suggestion: SuggestedReview) => void;
  refetch: () => void;
}

function SuggestedReviewsTab({ suggestions, isLoading, loadError, onCreateReview, refetch }: SuggestedReviewsTabProps) {
  const { t } = useTranslation();
  // Use table features for sorting and searching
  const {
    processedData: sortedSuggestions,
    searchQuery,
    setSearchQuery,
    requestSort,
    getSortDirection,
  } = useTableFeatures<SuggestedReview>({
    data: suggestions,
    searchFields: ['clientName', 'bookkeeperName'],
    defaultSort: { key: 'priorityScore', direction: 'desc' },
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size="large" label={t('suggested.loading')} />
      </div>
    );
  }

  if (loadError) {
    return (
      <Card style={{ padding: '24px' }}>
        <LoadFailed onRetry={() => refetch()} />
      </Card>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Text size={400} weight="semibold">
          {t('suggested.count', { count: sortedSuggestions.length })}
        </Text>
        <Button appearance="subtle" icon={<ArrowSyncRegular />} onClick={refetch}>
          {t('reviews.refresh')}
        </Button>
      </div>

      {/* Search */}
      <Card style={{ marginBottom: '16px' }}>
        <TableToolbar
          searchPlaceholder={t('suggested.search')}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          filters={[]}
          activeFilters={{}}
          onFilterChange={() => {}}
          resultCount={sortedSuggestions.length}
          totalCount={suggestions.length}
        />
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                sortKey="clientName"
                sortDirection={getSortDirection('clientName')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '200px' }}
              >
                {t('suggested.col.client')}
              </SortableHeader>
              <SortableHeader
                sortKey="bookkeeperName"
                sortDirection={getSortDirection('bookkeeperName')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '140px' }}
              >
                {t('suggested.col.bookkeeper')}
              </SortableHeader>
              <SortableHeader
                sortKey="priorityScore"
                sortDirection={getSortDirection('priorityScore')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '100px' }}
              >
                {t('suggested.col.priority')}
              </SortableHeader>
              <TableHeaderCell style={{ width: '200px' }}>{t('suggested.col.reason')}</TableHeaderCell>
              <SortableHeader
                sortKey="outstandingTasks"
                sortDirection={getSortDirection('outstandingTasks')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '80px' }}
              >
                {t('suggested.col.tasks')}
              </SortableHeader>
              <SortableHeader
                sortKey="nextTaxAppointment"
                sortDirection={getSortDirection('nextTaxAppointment')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '150px' }}
              >
                {t('suggested.col.nextTaxAppt')}
              </SortableHeader>
              <SortableHeader
                sortKey="qtrReviewDone"
                sortDirection={getSortDirection('qtrReviewDone')}
                onSort={(key) => requestSort(key as keyof SuggestedReview)}
                style={{ width: '80px' }}
              >
                {t('suggested.col.qtrReview')}
              </SortableHeader>
              <TableHeaderCell style={{ width: '100px' }}>{t('suggested.col.actions')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSuggestions.map(suggestion => (
              <TableRow key={suggestion.bookkeepingId}>
                <TableCell>
                  <Text weight="semibold">{suggestion.clientName}</Text>
                  {suggestion.avgMonthlyTransactions && (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {t('suggested.txnsPerMonth', { value: suggestion.avgMonthlyTransactions.toLocaleString(i18n.language) })}
                    </Text>
                  )}
                </TableCell>
                <TableCell>{suggestion.bookkeeperName}</TableCell>
                <TableCell>{getPriorityBadge(suggestion.priorityScore)}</TableCell>
                <TableCell>
                  <Text size={200}>{suggestion.priorityReason}</Text>
                </TableCell>
                <TableCell>
                  <Tooltip
                    content={
                      <div>
                        <Text size={200} weight="semibold" style={{ display: 'block' }}>{t('suggested.tasks', { period: taskWindowLabel(suggestion.taskCountSince, suggestion.taskCountPeriod, t('suggested.allTime')) })}</Text>
                        <Text size={200}>{t('suggested.outstanding', { count: suggestion.outstandingTasks })}</Text>
                        <br />
                        <Text size={200}>{t('suggested.completed', { count: suggestion.completedTasks })}</Text>
                        {suggestion.lastReviewEndDate && (
                          <>
                            <br />
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                              {t('suggested.lastReview', { date: formatCalendarDate(suggestion.lastReviewEndDate) })}
                            </Text>
                          </>
                        )}
                      </div>
                    }
                    relationship="description"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
                      <TaskListLtr24Regular style={{ fontSize: '16px' }} />
                      <Text size={200}>
                        {suggestion.outstandingTasks} / {suggestion.completedTasks + suggestion.outstandingTasks}
                      </Text>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {suggestion.nextTaxAppointment ? (
                    <Tooltip
                      content={
                        <div>
                          {suggestion.upcomingAppointments.map((appt, i) => (
                            <div key={i} style={{ marginBottom: '4px' }}>
                              <Text size={200} weight={appt.isTaxRelated ? 'semibold' : 'regular'}>
                                {formatInstant(appt.scheduledDate, 'monthDay')} - {appt.subject}
                                {appt.isTaxRelated && t('suggested.tax')}
                              </Text>
                            </div>
                          ))}
                        </div>
                      }
                      relationship="description"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}>
                        <CalendarRegular style={{ fontSize: '16px', color: tokens.colorPaletteRedForeground1 }} />
                        <Text size={200}>
                          {formatDistanceToNow(parseISO(suggestion.nextTaxAppointment), { addSuffix: true, locale: dateFnsLocale() })}
                        </Text>
                      </div>
                    </Tooltip>
                  ) : (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {t('suggested.noUpcoming')}
                    </Text>
                  )}
                </TableCell>
                <TableCell>
                  {suggestion.qtrReviewDone ? (
                    <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  ) : (
                    <DismissRegular style={{ color: tokens.colorPaletteRedForeground1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    appearance="primary"
                    size="small"
                    icon={<AddRegular />}
                    onClick={() => onCreateReview(suggestion)}
                  >
                    {t('suggested.review')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {sortedSuggestions.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <LightbulbRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
            <Text style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: '16px' }}>
              {suggestions.length === 0
                ? t('suggested.emptyNone')
                : t('suggested.emptyFiltered')}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
}

// Column catalog for the Active Reviews list — drives header filters, the client-side filter
// engine, and sort. Includes the new Reviewer column (`supervisorName`, already on every row).
//
// Labels and info are GETTERS (Spanish UI): this catalog is module-level, and every surface that
// reads `.label` — headers, filter panels, chips — must read it in the language of that moment.
const tl = (key: string) => i18n.t(key);
const option = (value: string, labelKey: string) => ({ value, get label() { return tl(labelKey); } });
/**
 * A self-review: the reviewer is also the client's bookkeeper (BL-089). Named here so it can be filtered and badged;
 * before this nothing in the app said it, and it showed only to someone reading two ids side by side (measured
 * 2026-07-26: 55 of 654 reviews, 25 of them completed). Both ids must be present: an unassigned review is not one.
 */
export function isSelfReview(r: Pick<ClientReview, 'supervisorId' | 'bookkeeperId'>): boolean {
  const reviewer = (r.supervisorId ?? '').trim().toLowerCase();
  const bookkeeper = (r.bookkeeperId ?? '').trim().toLowerCase();
  return reviewer !== '' && reviewer === bookkeeper;
}

const reviewColumns: FilterColumn<ClientReview>[] = [
  { key: 'client', get label() { return tl('reviews.col.client'); }, sortKey: 'client', filterType: 'text', getValue: (r) => r.clientName, get info() { return tl('reviews.col.clientInfo'); } },
  // C86: staff lookups, so one searchable multi-select each (options: the people in the loaded list). A blank name is NO
  // value, the "(has no value)" row, so the count and the filter agree on it.
  { key: 'bookkeeper', get label() { return tl('reviews.col.bookkeeper'); }, sortKey: 'bookkeeper', filterType: 'picklist', getValue: (r) => r.bookkeeperName || null, get info() { return tl('reviews.col.bookkeeperInfo'); } },
  { key: 'reviewer', get label() { return tl('reviews.col.reviewer'); }, sortKey: 'reviewer', filterType: 'picklist', getValue: (r) => r.supervisorName || null, get info() { return tl('reviews.col.reviewerInfo'); } },
  { key: 'period', get label() { return tl('reviews.col.period'); }, sortKey: 'period', filterType: 'date-range', dateOnly: true, getValue: (r) => r.periodStart, get info() { return tl('reviews.col.periodInfo'); } },
  {
    key: 'complexity', get label() { return tl('reviews.col.complexity'); }, sortKey: 'complexity', filterType: 'picklist',
    filterOptions: [
      option('low', 'complexity.label.low'), option('medium', 'complexity.label.medium'), option('high', 'complexity.label.high'),
    ],
    getValue: (r) => r.complexity ?? null,
    get info() { return tl('reviews.col.complexityInfo'); },
  },
  {
    key: 'status', get label() { return tl('reviews.col.status'); }, sortKey: 'status', filterType: 'picklist',
    filterOptions: [
      option('scheduled', 'reviewStatus.scheduled'), option('ready', 'reviewStatus.ready'),
      option('waiting-on-bookkeeping', 'reviewStatus.waitingOnBookkeeping'),
      option('in-progress', 'reviewStatus.inProgress'),
      option('completed', 'reviewStatus.completed'), option('cancelled', 'reviewStatus.cancelled'),
    ],
    // The DERIVED status drives filtering, sorting and the badge, so `scheduled` is a first-class
    // filter value. Falls back to the stored status if the API response predates the field.
    getValue: (r) => derivedStatus(r),
    get info() { return tl('reviews.col.statusInfo'); },
  },
  { key: 'taxAppt', get label() { return tl('reviews.col.taxAppt'); }, sortKey: 'taxAppt', filterType: 'date-range', getValue: (r) => r.nextTaxAppointment ?? null, get info() { return tl('reviews.col.taxApptInfo'); } },
  {
    key: 'selfReview', get label() { return tl('reviews.col.selfReview'); }, filterType: 'picklist',
    filterOptions: [option('yes', 'reviews.selfReview.yes'), option('no', 'reviews.selfReview.no')],
    getValue: (r) => (isSelfReview(r) ? 'yes' : 'no'),
    get info() { return tl('reviews.col.selfReviewInfo'); },
  },
];

// --- "My Active" default view helpers (supervision revamp Task 1) ------------------------------
// A supervisor opens Reviews to work their own in-flight reviews, so — when we can resolve who they
// are — the page lands on "My Active" (their in-progress + pending reviews) instead of the full org
// list. These pure helpers are unit-tested (ReviewsPage.test.tsx) and composed by the page below.

export const MY_ACTIVE_VIEW_KEY = 'my-active';

// --- scheduled / ready lifecycle (Christian's ruling 2026-07-25) -------------------------------
// Reviews are created at period start and are visible immediately, but a trimester review is not
// actionable for most of its window — a T2 review covering May 1 – Aug 31 has nothing to review in
// July, and prod carried ~230 of exactly those in the actionable queue. The API derives
// `scheduled` vs `ready` from (periodEnd, lead-in, now) and sends it as `lifecycleStatus`; the
// stored `status` vocabulary is untouched. The page filters, badges and counts on the derived value
// so the default queue shows only what is actually owed, with `scheduled` one filter click away.

/** The status the UI works in: the server-derived one, falling back to the stored one. */
export function derivedStatus(review: Pick<ClientReview, 'status' | 'lifecycleStatus'>): string {
  return review.lifecycleStatus ?? review.status;
}

/**
 * Statuses that count as an "active" review — a real, open piece of work someone owes NOW.
 * `scheduled` is open but deliberately excluded. `pending` remains here for a response that
 * predates `lifecycleStatus`, so an older API can never make the badge read 0.
 */
function isActiveStatus(status: string): boolean {
  return status === 'ready' || status === 'in-progress' || status === 'pending';
}

/** The Active-Reviews tab badge: only genuinely-open, ACTIONABLE reviews. */
export function countActiveReviews(
  reviews?: Pick<ClientReview, 'status' | 'lifecycleStatus'>[]
): number {
  return reviews?.filter((r) => isActiveStatus(derivedStatus(r))).length ?? 0;
}

/**
 * Rewrites a legacy `pending` status filter to `ready`. Bookmarks and cross-page deep links minted
 * before this change carry `filter.status.eq=pending`, and rows now report `ready`/`scheduled`
 * instead — without this they would silently show zero results. Returns the SAME object when there
 * is nothing to rewrite, so the caller can skip a no-op URL update.
 */
export function normalizeLegacyStatusFilters(
  filters: Record<string, string>
): Record<string, string> {
  let changed = false;
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'filter.status.eq' || key === 'filter.status.in') {
      const mapped = value
        .split(',')
        .map((v) => (v.trim() === 'pending' ? 'ready' : v.trim()))
        .join(',');
      if (mapped !== value) changed = true;
      next[key] = mapped;
    } else {
      next[key] = value;
    }
  }
  return changed ? next : filters;
}

/**
 * The view the page should land on for a fresh visit: `my-active` when we know who the caller is,
 * otherwise the broad `all` (never a blank page). This is the engine-neutral default decision; the
 * page seeds the corresponding filters into the URL once identity resolves.
 */
export function initialReviewViewKey(myName?: string): string {
  return myName ? MY_ACTIVE_VIEW_KEY : 'all';
}

// In-progress work sorts above ready, and both above scheduled; within a rank, the oldest period
// first (chip away at the backlog). periodStart is an ISO date string, so a lexical compare is a
// chronological compare.
const MY_ACTIVE_STATUS_RANK: Record<string, number> = {
  'in-progress': 0,
  ready: 1,
  pending: 1, // legacy response without a derived status ranks with `ready`
  'waiting-on-bookkeeping': 2, // period over, books still open: after the work that can be done now
  scheduled: 3, // never actionable yet — last, even if it leaks into a filtered view
};

/** Status-rank ordering applied only to the `my-active` view when the user hasn't chosen a sort. */
export function sortMyActiveReviews<
  T extends Pick<ClientReview, 'status' | 'lifecycleStatus' | 'periodStart'>
>(reviews: T[]): T[] {
  return [...reviews].sort((a, b) => {
    const ra = MY_ACTIVE_STATUS_RANK[derivedStatus(a)] ?? 2;
    const rb = MY_ACTIVE_STATUS_RANK[derivedStatus(b)] ?? 2;
    if (ra !== rb) return ra - rb;
    return a.periodStart.localeCompare(b.periodStart);
  });
}

/**
 * The quick-view pills. `my-active` is prepended (first pill) only when the caller is known; the
 * existing pills (`my`, `all`, `pending`, `in-progress`, `completed`) keep their keys unchanged so
 * cross-page deep links stay stable.
 */
// Labels are GETTERS: the views are memoised on the reviewer's name, so a label computed here would
// keep the language it was first built in after a toggle on the open page.
export function buildReviewViews(rawName?: string): SavedView[] {
  // A lookup's `eq` matches the stored name exactly (the text filter ignored case); both come from the same staff
  // record, and the stored one is trimmed (ReviewerIdentity), so this is too.
  const myName = rawName?.trim() || undefined;
  const base: SavedView[] = [
    { key: 'all', get label() { return i18n.t('reviews.views.all'); }, query: {} },
    // Key kept as `pending` for deep-link stability; it now selects the ACTIONABLE half of what
    // `pending` used to mean, which is what the pill always intended to show.
    { key: 'pending', get label() { return i18n.t('reviews.views.ready'); }, query: { 'filter.status.eq': 'ready' } },
    // Scheduled is visible and one click away — filtered out of the work queue, never hidden.
    { key: 'scheduled', get label() { return i18n.t('reviews.views.scheduled'); }, query: { 'filter.status.eq': 'scheduled' } },
    // DP-READY-3: out of the work queue (and the Active count), never hidden — one click away like scheduled.
    { key: 'waiting', get label() { return i18n.t('reviews.views.waiting'); }, query: { 'filter.status.eq': 'waiting-on-bookkeeping' } },
    { key: 'in-progress', get label() { return i18n.t('reviews.views.inProgress'); }, query: { 'filter.status.eq': 'in-progress' } },
    { key: 'completed', get label() { return i18n.t('reviews.views.completed'); }, query: { 'filter.status.eq': 'completed' } },
    // BL-089: a standing view, not a digest email: the reviews whose reviewer is also the bookkeeper.
    { key: 'self-review', get label() { return i18n.t('reviews.views.selfReview'); }, query: { 'filter.selfReview.eq': 'yes' } },
  ];
  const myView: SavedView = {
    key: 'my',
    get label() { return myName ? i18n.t('reviews.views.mineNamed', { name: myName }) : i18n.t('reviews.views.mine'); },
    query: myName ? { 'filter.reviewer.eq': myName } : {},
  };
  const myActive: SavedView[] = myName
    ? [{
        key: MY_ACTIVE_VIEW_KEY,
        get label() { return i18n.t('reviews.views.myActive'); },
        query: {
          'filter.reviewer.eq': myName,
          // The default work queue: ready and beyond. `scheduled` is excluded here — this is the
          // fix for the ~230 T2 reviews that inflated what supervisors thought they owed.
          'filter.status.in': 'in-progress,ready',
        },
      }]
    : [];
  return [...myActive, myView, ...base];
}

export default function ReviewsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const account = accounts[0];
  const currentUser = account ? { id: account.localAccountId, name: account.name || account.username } : null;

  const [activeTab, setActiveTab] = useState<'active' | 'suggested'>('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SuggestedReview | null>(null);

  const { data: reviews, isLoading, error, refetch } = useClientReviews();
  const { data: clients } = useBookkeepingClients();
  const { data: suggestions, isLoading: suggestionsLoading, error: suggestionsError, refetch: refetchSuggestions, dataUpdatedAt: suggestionsFetchedAt } = useSuggestedReviews();
  // The full staff roster. Used for identity resolution (`currentSystemUser` below) and for the
  // bookkeeper lookup — NOT for the reviewer picker, which has its own narrowed list. Keep it wide:
  // a non-reviewer must still be able to resolve their own row here or the My Active pill breaks.
  const { data: systemUsers } = useSystemUsers();
  // The reviewer picker's list — a strict subset (GET /staff-reviewers). See useReviewerEligibleStaff.
  const { data: reviewerOptions, error: reviewerOptionsError } = useReviewerEligibleStaff();
  const createReviewMutation = useCreateReview();

  // Reviewer assignments (Task 7) drive the create-dialog's default supervisor. These endpoints are
  // admin-gated; for non-admins the queries 403 and the data is simply absent, so the resolver
  // returns undefined and the dialog falls back to defaulting the supervisor to the current user.
  const { data: reviewerAssignments } = useReviewerAssignments();
  const { data: staffProfiles } = useStaffProfiles();
  const assignedReviewerSystemUserIdFor = useMemo(() => {
    const staffIdToSystemUser = new Map<string, string>();
    for (const p of staffProfiles ?? []) {
      if (p.systemUserId) staffIdToSystemUser.set(p.staffId, p.systemUserId);
    }
    const companyToStaffId = new Map<string, string>();
    for (const a of reviewerAssignments ?? []) {
      if (a.companyId) companyToStaffId.set(a.companyId.toLowerCase(), a.reviewerStaffId);
    }
    return (companyId?: string): string | undefined => {
      if (!companyId) return undefined;
      const staffId = companyToStaffId.get(companyId.toLowerCase());
      return staffId ? staffIdToSystemUser.get(staffId) : undefined;
    };
  }, [reviewerAssignments, staffProfiles]);

  // Find the current user's system user ID by matching email or Azure AD ID
  const currentSystemUser = useMemo(() => {
    if (!systemUsers || !account) return null;
    return systemUsers.find(user => {
      // Match by Azure AD Object ID
      if (user.azureactivedirectoryobjectid && account.localAccountId) {
        if (user.azureactivedirectoryobjectid === account.localAccountId) return true;
      }
      // Match by email
      if (user.internalemailaddress && account.username) {
        if (user.internalemailaddress.toLowerCase() === account.username.toLowerCase()) return true;
      }
      return false;
    });
  }, [systemUsers, account]);

  // Quick-view pills. `my-active` (in-progress + pending, filtered to me) is the first pill and the
  // fresh-visit default when we can resolve the caller — a supervisor opens Reviews to work their own
  // active reviews. The existing `my` pill (my reviews, all statuses) stays; `my` filters by Reviewer.
  const myName = currentSystemUser?.fullname;
  const reviewViews: SavedView[] = useMemo(() => buildReviewViews(myName), [myName]);

  // The fresh-landing default is "My Active" once the caller's identity resolves, and "All" before
  // that (or if it never does). It is the ENGINE's default view, so a bare /reviews URL applies its
  // filters without writing them (fleet contract v2 §11 RULE V3 — mount writes nothing; C43). The
  // previous design wrote `view=my-active` + its filters into the URL on landing, which stamped a
  // default over the address bar. "All" is then an explicit `view=all`, and any URL with a view or a
  // filter wins over the default, including after identity arrives late (RULE V4).
  const list = useListView<ClientReview>({
    defaultViewKey: initialReviewViewKey(myName),
    views: reviewViews,
    columns: reviewColumns,
    data: reviews,
    searchAccessors: [(r) => r.clientName, (r) => r.bookkeeperName, (r) => r.supervisorName],
  });
  const filteredReviews = list.processedData;
  const { filters: listFilters, setFilters } = list;

  // Legacy deep-link compatibility: a bookmark carrying `filter.status.eq=pending` predates the
  // scheduled/ready split and would now match nothing at all. Rewrite it once, on arrival, so an
  // old link lands on the queue it meant rather than on an empty table. One-shot, and a no-op for
  // every URL that carries no legacy value (normalize returns the same object reference).
  const didNormalizeLegacyFilters = useRef(false);
  useEffect(() => {
    if (didNormalizeLegacyFilters.current) return;
    didNormalizeLegacyFilters.current = true;
    const normalized = normalizeLegacyStatusFilters(listFilters);
    if (normalized !== listFilters) setFilters(normalized);
  }, [listFilters, setFilters]);

  // Status-rank ordering for the my-active view (in-progress before pending, then oldest period
  // first) — applied only when that view is active and the user hasn't picked an explicit sort.
  // A productivity drill-through (`drill=2026.T3.covered`) narrows the rows to exactly the reviews
  // the chart says are behind that segment — the same list, so the counts agree (spec §5).
  const { state: drillState, clear: clearDrill } = useProductivityDrill();
  const displayedReviews = useMemo(() => {
    const ordered = list.activeViewKey === MY_ACTIVE_VIEW_KEY && !list.sort
      ? sortMyActiveReviews(filteredReviews)
      : filteredReviews;
    return applyDrill(ordered, drillState, (r) => r.id);
  }, [filteredReviews, list.activeViewKey, list.sort, drillState]);

  // ── The grid's columns, in one place (contract v2 §13) ────────────────────────────────────
  // Rendering from a spec is what lets the user's column ORDER and WIDTH (per-user preference) and
  // the URL's GROUPING apply without duplicating every cell. Checklist and Language are not
  // filterable, so their menus offer only width and move (items a column does not support are
  // absent — C58).
  const gridColumns: { key: string; column: FilterColumn<ClientReview>; defaultWidth: number; cell: (r: ClientReview) => ReactNode }[] = [
    { key: 'client', column: reviewColumns[0], defaultWidth: 190, cell: (r) => <Text weight="semibold">{r.clientName}</Text> },
    { key: 'bookkeeper', column: reviewColumns[1], defaultWidth: 140, cell: (r) => r.bookkeeperName },
    {
      key: 'reviewer', column: reviewColumns[2], defaultWidth: 140,
      cell: (r) => (
        <>
          {r.supervisorName || <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>-</Text>}
          {isSelfReview(r) && (
            <Tooltip content={t('reviews.selfReview.badgeTip')} relationship="description">
              <Badge appearance="tint" color="warning" size="small" style={{ marginLeft: '6px' }}>
                {t('reviews.selfReview.badge')}
              </Badge>
            </Tooltip>
          )}
        </>
      ),
    },
    { key: 'period', column: reviewColumns[3], defaultWidth: 120, cell: (r) => <Text size={200}>{formatPeriodRange(r.periodStart, r.periodEnd)}</Text> },
    { key: 'complexity', column: reviewColumns[4], defaultWidth: 100, cell: (r) => getComplexityBadge(r.complexity) },
    { key: 'status', column: reviewColumns[5], defaultWidth: 100, cell: (r) => getStatusBadge(derivedStatus(r)) },
    {
      key: 'checklist', column: { key: 'checklist', label: t('reviews.col.checklist') }, defaultWidth: 100,
      cell: (r) => (
        <Text size={200}>
          {r.checklistItems.filter((i) => i.isChecked).length}/{r.checklistItems.length}
        </Text>
      ),
    },
    {
      key: 'taxAppt', column: reviewColumns[6], defaultWidth: 100,
      cell: (r) =>
        r.nextTaxAppointment ? (
          <Tooltip content={formatInstant(r.nextTaxAppointment, 'dayYearTime')} relationship="label">
            <Text size={200}>{formatInstant(r.nextTaxAppointment, 'monthDay')}</Text>
          </Tooltip>
        ) : (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>-</Text>
        ),
    },
    {
      key: 'language', column: { key: 'language', label: t('reviews.col.language') }, defaultWidth: 80,
      cell: (r) => <ClientLanguageBadge language={r.clientLanguage} />,
    },
  ];
  const columnPrefs = useColumnPrefs('reviews', currentSystemUser?.systemuserid, gridColumns.map((c) => c.key));
  const orderedGridColumns = columnPrefs.order
    .map((key) => gridColumns.find((c) => c.key === key))
    .filter((c): c is (typeof gridColumns)[number] => !!c);

  // GROUP BY (§13.3): partitions the already filtered AND sorted rows, so filtering selects and
  // grouping only arranges (M6), and rows keep the active sort inside each group (M7).
  const reviewGroups = useMemo((): { key: string; label: string | null; rows: ClientReview[] }[] => {
    const groupColumn = list.group ? reviewColumns.find((c) => c.key === list.group!.col) : undefined;
    if (!groupColumn || !isGroupable(groupColumn)) return [{ key: '', label: null, rows: displayedReviews }];
    return groupRows(displayedReviews, groupColumn, list.group!.granularity);
  }, [displayedReviews, list.group]);

  // Bulk actions (docs/specs/reviews-bulk-actions.md §1): a set of ids over the VISIBLE rows. A filter
  // that hides a selected row deselects it, so a bulk action never reaches a row the user cannot see.
  const selection = useReviewSelection(displayedReviews.map((r) => r.id));
  const selectedReviews = displayedReviews.filter((r) => selection.isSelected(r.id));

  const handleCreateReview = async (data: {
    bookkeepingId: string;
    clientId: string;
    clientName: string;
    bookkeeperId: string;
    bookkeeperName: string;
    supervisorId: string;
    supervisorName: string;
    xeroLink?: string;
    avgMonthlyTransactions?: number;
    periodStart: string;
    periodEnd: string;
    reviewType: ReviewType;
  }) => {
    // Graded automation (docs/specs/graded-automation.md §1): a create from the Suggested tab carries what the
    // engine had ranked. `suggestions` is the server's order; the table sorts a copy.
    const fromSuggestion = selectedSuggestion
      ? buildSuggestionSnapshot(suggestions ?? [], selectedSuggestion, suggestionsFetchedAt, BUILD_COMMIT)
      : undefined;
    const createdReview = await createReviewMutation.mutateAsync(fromSuggestion ? { ...data, fromSuggestion } : data);
    // Refresh both reviews list and suggestions after creating a review
    refetch();
    refetchSuggestions();
    // programmatic-nav: after create, the new review's page.
    navigate(`/reviews/${createdReview.id}`);
  };

  const handleCreateFromSuggestion = (suggestion: SuggestedReview) => {
    setSelectedSuggestion(suggestion);
    setShowCreateDialog(true);
  };


  if (isLoading && activeTab === 'active') {
    return <PageSkeleton variant="rows" />;
  }

  if (error && activeTab === 'active') {
    return (
      <Card style={{ padding: '24px', textAlign: 'center' }}>
        <Title1>{t('reviews.loadError.title')}</Title1>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          {error instanceof Error ? error.message : t('reviews.loadError.body')}
        </Text>
        <Button appearance="primary" onClick={() => refetch()} style={{ marginTop: '16px' }}>
          {t('reviews.loadError.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <div>
      <MirrorFreshnessBanner />
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title1>{t('reviews.title')}</Title1>
            <Text style={{ color: tokens.colorNeutralForeground3 }}>
              {t('reviews.subtitle')}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="subtle"
              icon={<ArrowSyncRegular />}
              onClick={() => activeTab === 'active' ? refetch() : refetchSuggestions()}
            >
              {t('reviews.refresh')}
            </Button>
            <Button
              appearance="primary"
              icon={<AddRegular />}
              onClick={() => {
                setSelectedSuggestion(null);
                setShowCreateDialog(true);
              }}
            >
              {t('reviews.new')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card style={{ marginBottom: '16px' }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => setActiveTab(data.value as 'active' | 'suggested')}
        >
          <Tab value="active" icon={<DocumentSearchRegular />}>
            {t('reviews.tabs.active', { count: reviews === undefined ? (error ? '—' : '…') : countActiveReviews(reviews) })}
          </Tab>
          <Tab value="suggested" icon={<LightbulbRegular />}>
            {t('reviews.tabs.suggested', { count: suggestions === undefined ? (suggestionsError ? '—' : '…') : suggestions.length })}
          </Tab>
        </TabList>
      </Card>

      {activeTab === 'suggested' ? (
        <SuggestedReviewsTab
          suggestions={suggestions || []}
          isLoading={suggestionsLoading}
          loadError={suggestions === undefined ? suggestionsError : null}
          onCreateReview={handleCreateFromSuggestion}
          refetch={refetchSuggestions}
        />
      ) : (
        <>
      {/* Quick-view pills + search (replaces the old stat tiles + default-on "My Reviews" switch) */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '12px 16px' }}>
          <div style={{ marginRight: 'auto' }}>
            <QuickViewPills views={reviewViews} activeKey={list.activeViewKey} onSelect={list.selectView} />
          </div>
          <Input
            style={{ minWidth: '260px' }}
            placeholder={t('reviews.search')}
            value={list.search}
            onChange={(_, d) => list.setSearch(d.value)}
            contentBefore={<SearchRegular />}
            contentAfter={
              list.search ? (
                <Button appearance="transparent" icon={<DismissRegular />} size="small"
                  onClick={() => list.setSearch('')} aria-label={t('reviews.clearSearch')} />
              ) : undefined
            }
          />
        </div>
      </Card>

      <ActiveFiltersChips columns={reviewColumns} value={list.filters} onChange={list.setFilters} />

      <DrillBanner state={drillState} shown={displayedReviews.length} noun="review" onClear={clearDrill} />

      <BulkActionBar
        selection={selection}
        rows={selectedReviews}
        reviewers={reviewerOptions ?? []}
        staffProfiles={staffProfiles ?? []}
      />

      {/* Reviews Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              {/* Selection is not a data column: no header menu, never moved or hidden (bulk spec §1). */}
              <TableHeaderCell style={{ width: '40px' }}>
                <Checkbox
                  checked={selection.allVisibleSelected ? true : selection.someVisibleSelected ? 'mixed' : false}
                  onChange={(_, d) => selection.setAllVisible(d.checked === true)}
                  aria-label={t('reviews.selectAll', { count: displayedReviews.length })}
                  disabled={displayedReviews.length === 0}
                />
              </TableHeaderCell>
              {/* Contract v2 §13: every data column carries the header menu. Order and width are this
                  user's (a per-user preference, never the URL — M2); Actions stays fixed last. */}
              {orderedGridColumns.map((gc) => (
                <ListColumnHeader
                  key={gc.key}
                  column={gc.column}
                  sort={list.sort}
                  onSortChange={list.setSort}
                  filters={list.filters}
                  onFiltersChange={list.setFilters}
                  getFacetCounts={() => list.facetCountsFor(gc.column.key)}
                  getLookupOptions={() => list.lookupOptionsFor(gc.column.key)}
                  style={{ width: `${gc.defaultWidth}px` }}
                  group={list.group}
                  onGroupChange={list.setGroup}
                  width={columnPrefs.widths[gc.key]}
                  defaultWidth={gc.defaultWidth}
                  onWidthChange={(px) => columnPrefs.setWidth(gc.key, px)}
                  canMoveLeft={columnPrefs.canMoveLeft(gc.key)}
                  canMoveRight={columnPrefs.canMoveRight(gc.key)}
                  onMoveLeft={() => columnPrefs.moveLeft(gc.key)}
                  onMoveRight={() => columnPrefs.moveRight(gc.key)}
                />
              ))}
              <TableHeaderCell style={{ width: '80px' }}>{t('reviews.actions')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviewGroups.map((g) => (
              <Fragment key={g.key || 'ungrouped'}>
                {g.label !== null ? (
                  // A restored grouping renders EXPANDED (V6): no click is needed to see the rows.
                  <TableRow aria-label={t('reviews.group', { label: g.label, count: g.rows.length })}>
                    <TableCell colSpan={orderedGridColumns.length + 2}>
                      <Text weight="semibold">{g.label}</Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginLeft: '8px' }}>
                        {g.rows.length}
                      </Text>
                    </TableCell>
                  </TableRow>
                ) : null}
                {g.rows.map((review) => (
                  <TableRow
                    key={review.id}
                    // Real links (fleet ruling 2026-10-29): the row's Open link is stretched over the whole row, so a
                    // click anywhere opens the review and right-click anywhere opens it in a new tab.
                    className={ROW_LINK_HOST}
                    style={{ cursor: 'pointer' }}
                    // hover only — onFocus would fire per-row during keyboard traversal (each row has a focusable button)
                    onMouseEnter={() => void prefetchClientReview(queryClient, review.id)}
                    aria-selected={selection.isSelected(review.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selection.isSelected(review.id)}
                        onChange={() => selection.toggle(review.id)}
                        aria-label={t('reviews.selectOne', { client: review.clientName })}
                      />
                    </TableCell>
                    {orderedGridColumns.map((gc) => (
                      <TableCell key={gc.key}>{gc.cell(review)}</TableCell>
                    ))}
                    <TableCell>
                      <RowLink href={`/reviews/${review.id}`} aria-label={t('reviews.openOne', { client: review.clientName })}>
                        <OpenRegular />
                      </RowLink>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>

        {displayedReviews.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <DocumentSearchRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
            <Text style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: '16px' }}>
              {reviews?.length === 0
                ? t('reviews.emptyNone')
                : t('reviews.emptyFiltered')}
            </Text>
          </div>
        )}
      </Card>
        </>
      )}

      {/* Create Review Dialog */}
      <CreateReviewDialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedSuggestion(null);
        }}
        clients={clients || []}
        existingReviews={reviews || []}
        existingReviewsKnown={reviews !== undefined}
        reviewerOptions={reviewerOptions || []}
        reviewerOptionsFailed={!reviewerOptions && !!reviewerOptionsError}
        onSubmit={handleCreateReview}
        preselectedClient={selectedSuggestion}
        isSubmitting={createReviewMutation.isPending}
        currentUser={currentUser}
        currentSystemUser={currentSystemUser}
        assignedReviewerSystemUserIdFor={assignedReviewerSystemUserIdFor}
      />
    </div>
  );
}
